// @flow

import update from 'immutability-helper';
import type {ReduxActionType} from "../../flow/ReduxActionType";

const INITIAL_STATE = {
    // The detail response: one entry per version of the text (see handoff-dashboard.md §3)
    translation: {
        id: 0,
        hash: '',
        source_language: '',
        target_language: '',
        original: '',
        translations: [],
        active: null,
        active_id: null,
    },
    loaded: false,
    // Radio currently chosen by the user: 'base' | 'ai' | 'manual' | null
    selectedLevel: null,
    // Level recorded by the last save (or on load): `selected` of the detail response. It differs from
    // `active` (the level SERVED) while the AI policy overrides the choice.
    savedLevel: null,
    // Content of the manual translation editor
    manualText: '',
    // true once the user typed/copied something in the manual editor
    manualEdited: false,
    // true once the user changed the selected level or the manual text
    dirty: false,
    // Bumped to remount the (uncontrolled) editor when its content is replaced
    editorKey: 0,
    is_inline: false,
    // true when a manual translation exists but the AI policy "Override manual translations" makes the
    // AI one win (derived from the detail response, see hydrate)
    overrideManual: false,
    // true when the "Override manual and selected translations" setting makes the newest AI translation win over
    // any choice (API `overridden_by_policy`), whether or not a manual translation exists
    policyOverride: false,
    saving: false,
    deleting: false,
    // id of the AI version being restored, null when idle
    restoring: null,
    // true while the recorded choice is being cleared
    resetting: false,
    saved: false
};

type State = { type?: string };

// Matches both `<a i=0>` (API) and `<a i="0">` (serialised by the inline editor)
const INLINE_REGEX = /<a i=["']?[0-9]+["']?[^>]*>.*?<\/a>/s;

/**
 * Derive the editor state from a detail response.
 * `keepManualText` preserves what the user typed (used after a restore).
 */
const hydrate = (state: Object, translation: Object, keepManualText: boolean = false) => {
    const versions = Array.isArray(translation.translations) ? translation.translations : [];
    const manual = versions.find((version) => version.type === 'manual');

    const is_inline = INLINE_REGEX.test(translation.original || '') ||
        versions.some((version) => INLINE_REGEX.test(version.text || ''));

    const keep = keepManualText && state.manualEdited;

    // The recorded choice wins over the served level: the API reports the level SERVED in `active`,
    // which stays 'ai' while the policy overrides a choice of 'manual'. A choice of 'manual' whose
    // text is gone cannot be shown, so it falls back to the served level.
    const selected = translation.selected && (translation.selected !== 'manual' || manual)
        ? translation.selected
        : (translation.active || null);

    // `overridden_by_policy` is the API's word that the "Override manual translations" setting is on
    // and the text has an AI row, so the AI one wins over the manual one and over any choice.
    const overrideManual = !!manual && translation.overridden_by_policy === true;
    const policyOverride = translation.overridden_by_policy === true;

    return {
        ...state,
        translation: {...translation, translations: versions},
        loaded: true,
        selectedLevel: selected,
        savedLevel: selected,
        overrideManual,
        policyOverride,
        manualText: keep ? state.manualText : (manual ? manual.text : ''),
        manualEdited: keep,
        dirty: false,
        is_inline,
    };
};

const Translation = (state: State = INITIAL_STATE, action: ReduxActionType) => {
    switch (action.type) {
        case 'TRANSLATION_LOAD':
            return {...INITIAL_STATE};

        case 'TRANSLATION_LOADED':
            return hydrate(state, action.payload);

        case 'TRANSLATION_CHANGE_TYPE':
            return {...state, translation: {type: action.payload,}};

        case 'TRANSLATION_CLICK_INSERTION':
            return {...state,};

        case 'TRANSLATION_CHANGE_INPUT':
            // The only editable input is the manual translation; typing in it selects the manual level
            if (action.payload.name === 'translation') {
                return {
                    ...state,
                    manualText: action.payload.value,
                    manualEdited: true,
                    selectedLevel: 'manual',
                    dirty: true,
                };
            }
            return update(state, {translation: {[action.payload.name]: {$set: action.payload.value}}});

        case 'TRANSLATION_SELECT_LEVEL':
            return {...state, selectedLevel: action.payload, dirty: true};

        case 'TRANSLATION_COPY':
            return {
                ...state,
                manualText: action.payload,
                manualEdited: true,
                selectedLevel: 'manual',
                dirty: true,
                editorKey: state.editorKey + 1,
            };

        case 'TRANSLATION_SAVE':
            return {...state, saved: false, saving: true};

        case 'TRANSLATION_SAVED':
            return {...(action.payload ? hydrate(state, action.payload) : state), saved: false, saving: false};

        case 'TRANSLATION_SAVED_AND_QUIT':
            return {...INITIAL_STATE, saved: true, saving: false};

        case 'TRANSLATION_RESTORE':
            return {...state, restoring: action.payload.active_id};

        case 'TRANSLATION_RESTORED':
            return {...hydrate(state, action.payload, true), restoring: null};

        case 'TRANSLATION_RESET_CHOICE':
            return {...state, resetting: true};

        case 'TRANSLATION_CHOICE_RESET':
            return {...hydrate(state, action.payload, true), resetting: false};

        case 'TRANSLATION_DELETE':
            return {...state, deleting: true};

        case 'TRANSLATION_DELETED':
            return {...INITIAL_STATE, saved: true};

        case 'TRANSLATION_CLOSED':
            return {...INITIAL_STATE};

        case 'TRANSLATION_REQUEST_FAILED':
            return {...state, saving: false, deleting: false, restoring: null, resetting: false};

        default:
            return {...state};
    }
};

export default Translation;
